import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useRef, useEffect } from "react";
import cn from "classnames";
import useOutsideClick from "@hooks/useOutsideClick";
import ChevronSm from "@icons/ChevronSm";
import Close from "@icons/Close";
import ToggleButton from "@components/button/ToggleButton";
import useBrowserSearchParams from "@hooks/useBrowserSearchParams";
import useTranslations from "@hooks/useTranslations";
const FilterSelector = ({ initialFilters, labelPlaceholder, onChange, className, oneColLayout, onFiltersModified, isClearable, useBrowserParams }) => {
    const [filters, setFilters] = useState([]);
    const filterSelectRefs = useRef([]);
    const { toggleSearchParamsInURL, searchParamsObj, urlHasParams } = useBrowserSearchParams();
    const { t } = useTranslations();
    const checkSearchParamsInUrlAndSetFilters = () => {
        const modifiedFilters = initialFilters.map((filter) => {
            if (searchParamsObj.hasOwnProperty(filter.name)) {
                return {
                    ...filter,
                    options: filter.options.map((option) => ({ ...option, isActive: option.value === searchParamsObj[filter.name] }))
                };
            }
            else {
                return filter;
            }
        });
        setFilters(modifiedFilters);
    };
    const intializeFilterOptionsWithDefaultValue = () => {
        setFilters(initialFilters.map((filter) => {
            return {
                ...filter,
                options: filter.defaultValue !== undefined ? filter.options.map((option) => ({ ...option, isActive: option.value === filter.defaultValue })) : filter.options
            };
        }));
    };
    useEffect(() => {
        if (urlHasParams && useBrowserParams) {
            checkSearchParamsInUrlAndSetFilters();
            return;
        }
        intializeFilterOptionsWithDefaultValue();
    }, [urlHasParams]);
    const toggleFilterVisibility = (filterName, hideAll = false) => {
        // Additional condtion to avoid Memory leak; To watch behaviour
        const openedFilters = filters.filter((filter) => filter.isOpen);
        if (hideAll && openedFilters.length === 0) {
            return;
        }
        setFilters((prevFilters) => prevFilters.map((filter) => {
            const matchedFilter = filter.name === filterName;
            const ifNotHideAll = matchedFilter ? !!!(filter === null || filter === void 0 ? void 0 : filter.isOpen) : false;
            return {
                ...filter,
                isOpen: hideAll ? false : ifNotHideAll
            };
        }));
    };
    const modifyFilterState = (filterName, filterValue) => filters.map((filter) => {
        const matchedFilter = filter.name === filterName;
        return {
            ...filter,
            isOpen: matchedFilter ? false : filter.isOpen,
            options: matchedFilter
                ? filter.options.map((option) => {
                    const toggleIsActiveValue = isClearable ? !!!(option === null || option === void 0 ? void 0 : option.isActive) : true;
                    return {
                        ...option,
                        isActive: option.value === filterValue ? toggleIsActiveValue : false
                    };
                })
                : filter.options
        };
    });
    const handleFilterValueChanges = (filterName, filterValue) => {
        onChange && onChange(filterName, filterValue);
        useBrowserParams && toggleSearchParamsInURL(filterName, filterValue);
        if (typeof filterValue === "boolean") {
            return;
        }
        const modifiedFilters = modifyFilterState(filterName, filterValue);
        setFilters([...modifiedFilters]);
        onFiltersModified &&
            onFiltersModified(modifiedFilters
                .filter((filterItem) => (filterItem === null || filterItem === void 0 ? void 0 : filterItem.options) && filterItem.options.some((option) => option.isActive))
                .map((filterItem) => ({ name: filterItem.name, options: filterItem.options.filter((option) => option.isActive).map((option) => option.value) })));
    };
    useOutsideClick({
        refs: filterSelectRefs,
        callback: () => toggleFilterVisibility(undefined, true)
    });
    return (_jsxs("div", { className: cn("filter-container", className, oneColLayout && "filter-container--single-col", !isClearable && "filter-container--not-clearable"), children: [_jsx("label", { className: "filter-container-label", children: labelPlaceholder }), filters.map(({ name, options, isOpen, type, title }, index) => {
                var _a;
                return (_jsxs(_Fragment, { children: [type === "select" ? (_jsxs("div", { className: "filter-container__select", ref: (ref) => (filterSelectRefs.current[index] = ref), children: [_jsxs("button", { className: cn("filter-container__select-btn group/item", isOpen && "bg-white"), type: "button", onClick: () => toggleFilterVisibility(name), children: [_jsx("span", { className: "filter-container__select-value", children: _jsx("span", { className: "hover-underline", children: (options && t((_a = options.find((opt) => opt.isActive)) === null || _a === void 0 ? void 0 : _a.label)) || t(title) }) }), _jsx(ChevronSm, { className: cn("mx-2 transition duration-300", isOpen && "rotate-180") })] }), _jsxs("ul", { className: cn("filter-container__select__options", !isOpen && "hidden"), children: [!oneColLayout && _jsx("li", { className: "filter-container__select__options__item-type", children: t(title) }), options &&
                                            options.map(({ label, value, isActive }) => (_jsxs("li", { className: cn("filter-container__select__options__item-value", isActive && !isClearable && "font-bold"), onClick: () => handleFilterValueChanges(name, value), children: [_jsx("span", { title: t(label), children: t(label) }), isActive && isClearable && _jsx(Close, { className: "ml-1.5" })] }, `${name}_${label}`)))] })] }, name)) : null, type === "toggle" ? (_jsx(ToggleButton, { className: "px-2 py-2", placeholder: t(title), id: name, onChange: (checked) => handleFilterValueChanges(name, checked) }, name)) : null] }));
            })] }));
};
export default FilterSelector;
